"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  number: "01",
  question: "How does revenue management improve hotel profitability?",
  answer: "Revenue management optimizes your pricing and inventory availability in real-time based on market demand, competitor activity, and historical data. By ensuring the right room is sold to the right guest at the right price, we maximize your RevPAR and overall bottom-line profitability."
}, {
  number: "02",
  question: "What is the difference between an OTA audit and a full revenue audit?",
  answer: "An OTA audit focuses specifically on your visibility, content quality, and conversion rates on platforms like Booking.com and Expedia. A full revenue audit is a comprehensive deep-dive into your entire tech stack, pricing strategy, distribution channels, and operational workflows to identify hidden revenue leaks."
}, {
  number: "03",
  question: "How do you select the right hospitality tech for my property?",
  answer: "We evaluate your current infrastructure, operational pain points, and long-term goals. We then recommend and oversee the implementation of PMS, Channel Managers, and RMS solutions that integrate seamlessly to automate your processes and provide actionable data insights."
}, {
  number: "04",
  question: "Can you help independent hotels compete with major chains?",
  answer: "Absolutely. We specialize in leveling the playing field for independent properties by implementing enterprise-level revenue strategies, sophisticated OTA management, and data-driven marketing tactics that increase direct bookings and market share."
}, {
  number: "05",
  question: "What is the typical timeline for seeing revenue improvements?",
  answer: "While immediate adjustments to OTA listings and pricing can yield quick wins, a comprehensive revenue management strategy typically shows significant, sustainable growth in RevPAR and GOPPAR within 3 to 6 months of implementation."
}, {
  number: "06",
  question: "Do you provide ongoing support or one-time consulting?",
  answer: "We offer both. We provide one-time revenue audits and tech implementation projects, as well as ongoing monthly revenue management retainers to ensure your property remains competitive in a constantly shifting market."
}, {
  number: "07",
  question: "How do you handle OTA rate parity and commission issues?",
  answer: "We conduct a thorough analysis of your current distribution agreements and rate parity status. We then implement strategies to optimize your channel mix, reduce reliance on high-commission OTAs, and drive more high-margin direct bookings."
}, {
  number: "08",
  question: "How do I get started with a consultation?",
  answer: "You can schedule a discovery call through our Contact page. We will discuss your property's current performance, your specific challenges, and how our data-driven strategies can help you achieve your revenue goals."
}];
export default function Auditcomparisonfaq() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const list = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3235" id="auditcomparisonfaq" className="bg-background text-foreground py-24 md:py-32 lg:py-[140px] px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-24 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-8">
            <p className="font-default text-xs md:text-sm uppercase tracking-widest text-foreground/70 mb-6">
              — FAQ / REVENUE INSIGHTS
            </p>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-foreground tracking-tight">
              Expert answers for{" "}
              <span className="italic font-light">hospitality</span> growth.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end lg:items-end">
            <Link to="/ContactPage" className="font-default text-xs uppercase tracking-widest text-foreground hover:text-secondary transition-colors duration-300 inline-flex items-center gap-2 border-b border-foreground/30 hover:border-foreground pb-1">
              Need specific advice? Contact us →
            </Link>
          </div>
        </div>

        {}
        <div ref={list.ref} className={`max-w-[880px] mx-auto transition-all duration-700 ease-out delay-150 ${list.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="group border-b border-border last:border-b-0 data-[state=open]:border-l-2 data-[state=open]:border-l-secondary data-[state=open]:pl-6 transition-all duration-300">
                <AccordionTrigger className="flex items-start justify-between gap-6 py-7 md:py-8 hover:no-underline [&>svg]:hidden text-left">
                  <div className="flex items-start gap-6 md:gap-10 flex-1">
                    <span className="font-default text-xs md:text-sm text-foreground/50 pt-2 shrink-0 tracking-wider">
                      {faq.number}
                    </span>
                    <span className="font-default font-semibold text-lg md:text-[22px] leading-snug text-foreground">
                      {faq.question}
                    </span>
                  </div>
                  <Plus className="size-5 md:size-6 text-secondary shrink-0 mt-2 transition-transform duration-400 ease-out group-data-[state=open]:rotate-45" strokeWidth={1} />
                </AccordionTrigger>
                <AccordionContent className="pl-12 md:pl-[72px] pr-10 md:pr-12 pb-8">
                  <p className="font-default text-base text-foreground/75 leading-[1.8]">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}